// src/components/Stripe/SuccessPage.js
import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Fade,
  Grow,
  Divider,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import API from '../config';

// Using only the custom confetti component

// Simple custom confetti component as a fallback
const SimpleConfetti = ({ isActive }) => {
  const theme = useTheme();
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    if (!isActive) return;
    
    // Create confetti particles
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.info.main,
    ];
    
    const newParticles = Array.from({ length: 50 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: -20 - Math.random() * 80,
      size: 5 + Math.random() * 10,
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      opacity: 1,
    }));
    
    setParticles(newParticles);
    
    // Clean up
    return () => setParticles([]);
  }, [isActive, theme]);
  
  if (!isActive || particles.length === 0) return null;
  
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1500,
        overflow: 'hidden',
      }}
    >
      {particles.map((particle) => (
        <Box
          key={particle.id}
          sx={{
            position: 'absolute',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            borderRadius: '50%',
            transform: `rotate(${particle.rotation}deg)`,
            opacity: particle.opacity,
            animation: 'fall 3s linear forwards',
            '@keyframes fall': {
              to: {
                top: '120%',
                opacity: 0,
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

export default function SuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { authData } = useContext(AuthContext);
  const token = authData?.access_token;
  
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [authCheckAttempts, setAuthCheckAttempts] = useState(0);
  
  // Get session ID from URL query params (Stripe automatically adds this)
  const sessionId = new URLSearchParams(location.search).get('session_id');
  const paymentId = location.state?.paymentId || new URLSearchParams(location.search).get('payment_intent');
  const amount = location.state?.amount || '';
  
  useEffect(() => {
    // Wait for authData to be available after Stripe redirect
    // This prevents race condition where authData isn't ready yet after redirect
    if (authData === undefined) {
      // AuthContext is still loading, wait a bit before checking
      if (authCheckAttempts < 15) {
        const timer = setTimeout(() => {
          setAuthCheckAttempts(prev => prev + 1);
        }, 200);
        return () => clearTimeout(timer);
      }
    }
    
    // Only redirect to login if we're sure authData is null (not loading)
    if (!token && authData === null) {
      navigate('/login');
      return;
    }
    
    // If we don't have a token yet but authData is still loading, keep waiting
    if (!token && authData === undefined) {
      return; // Still waiting for auth to initialize
    }
    
    // If we have a token, proceed with verification
    if (!token) {
      return; // Still waiting for auth
    }
    
    // Verify the session and activate subscription
    const verifySession = async () => {
      if (!sessionId) {
        setError('No session ID found. Please contact support if you completed payment.');
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(
          `${API}/api/subscription/verify-session/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setSubscriptionData(response.data.subscription);
        setShowConfetti(true);
        setLoading(false);
        
        // Track conversion in Rewardful using JavaScript API (primary method)
        // This automatically links to the referral via Rewardful's cookie
        // IMPORTANT: This runs when user returns from Stripe checkout to the success page
        // Try multiple sources for email (backend response, authData, or wait for it)
        const customerEmail = response.data?.customer_email || 
                             authData?.user?.email || 
                             response.data?.subscription?.customer_email;
        
        // Track conversion in Rewardful - wait for email if needed
        // NOTE: Frontend conversion is optional - backend webhook handles the actual conversion
        // This is just a fallback. The backend uses visitor_id which works even if emails don't match.
        const trackConversion = async (email) => {
          if (!email) {
            return;
          }
          
          // Also try backend conversion tracking as backup (more reliable)
          try {
            const visitorId = localStorage.getItem('rewardful_visitor_id');
            if (visitorId) {
              // The backend verify-session endpoint should handle this, but we can also call it explicitly
              // The verify-session endpoint already tracks conversion, so this is just for logging
            }
          } catch (e) {
            // Silently handle visitor_id retrieval errors
          }
          
          // Function to track conversion - will be called multiple times to ensure it works
          // This is optional - backend webhook is the primary method
          const trackRewardfulConversion = () => {
            if (window.rewardful) {
              try {
                // Get visitor_id from localStorage if available (more reliable than email)
                const visitorId = localStorage.getItem('rewardful_visitor_id');
                
                // Try conversion with visitor_id if available (most reliable)
                if (visitorId) {
                  window.rewardful('convert', { visitor_id: visitorId, email: email });
                  return true;
                } else {
                  // Fallback: try conversion with email (may fail if Stripe email differs)
                  window.rewardful('convert', { email: email });
                  return true;
                }
              } catch (convertError) {
                // Silently handle - backend webhook will handle conversion
                return false;
              }
            }
            return false;
          };
          
          // Try immediate conversion if Rewardful is already loaded
          let conversionTracked = trackRewardfulConversion();
          
          // Also set up ready callback in case Rewardful isn't loaded yet
          // This handles the case where the script loads after page load
          if (window.rewardful) {
            try {
              window.rewardful('ready', function() {
                if (!conversionTracked) {
                  trackRewardfulConversion();
                }
              });
            } catch (e) {
              // Silently handle callback setup errors
            }
          }
          
          // If Rewardful isn't loaded yet, wait for it and try again
          if (!window.rewardful) {
            let attempts = 0;
            const checkRewardful = setInterval(() => {
              attempts++;
              if (window.rewardful) {
                clearInterval(checkRewardful);
                trackRewardfulConversion();
              } else if (attempts > 20) {
                // Give up after 4 seconds (20 * 200ms)
                clearInterval(checkRewardful);
              }
            }, 200);
          }
        };
        
        // Track conversion if we have email - try multiple times with different sources
        if (customerEmail) {
          console.log('✅ Email available from response, tracking conversion:', customerEmail);
          trackConversion(customerEmail);
        } else {
          // Wait a bit for authData to load, then try again
          console.log('⏳ Email not available yet, waiting for authData...');
          console.log('📊 Current authData:', { 
            hasAuthData: !!authData, 
            hasUser: !!authData?.user, 
            userEmail: authData?.user?.email,
            responseEmail: response.data?.customer_email,
            fullResponseKeys: Object.keys(response.data || {})
          });
          
          // Try multiple times with increasing delays
          const tryTrackConversion = (attempt = 1) => {
            // Try all possible sources again
            const retryEmail = response.data?.customer_email ||
                              authData?.user?.email || 
                              response.data?.subscription?.customer_email ||
                              customerEmail;
            
            if (retryEmail) {
              console.log(`✅ Email available on attempt ${attempt}, tracking conversion:`, retryEmail);
              trackConversion(retryEmail);
            } else if (attempt < 5) {
              // Try up to 5 times (5 seconds total)
              console.log(`⏳ Attempt ${attempt}/5: Still waiting for email...`);
              setTimeout(() => tryTrackConversion(attempt + 1), 1000);
            } else {
              console.warn('⚠️ Email still not available after 5 attempts - conversion will be tracked via backend webhook');
              console.warn('📊 Final state:', { 
                authData: !!authData, 
                userEmail: authData?.user?.email,
                responseCustomerEmail: response.data?.customer_email,
                responseDataKeys: Object.keys(response.data || {}),
                fullResponse: response.data
              });
            }
          };
          
          tryTrackConversion();
        }
        
        // Note: Backend conversion tracking via webhook is the backup method
        // The JavaScript method above is the primary and recommended approach
      } catch (err) {
        console.error('Error verifying session:', err);
        setError(
          err.response?.data?.error || 
          'Failed to verify your subscription. Please contact support or check your dashboard.'
        );
        setLoading(false);
      }
    };
    
    verifySession();
  }, [token, sessionId, navigate, authData, authCheckAttempts]);
  
  // Show loading state while waiting for auth or verifying session
  if (loading || (authData === undefined && authCheckAttempts < 15)) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '60vh' 
      }}>
        <CircularProgress size={48} thickness={4} />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 500 }}>
          {authData === undefined 
            ? 'Loading your account...' 
            : 'Confirming your payment and activating subscription...'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This may take a few moments
        </Typography>
      </Box>
    );
  }
  
  // Show error state if verification failed
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Fade in={true} timeout={800}>
          <Paper 
            elevation={4} 
            sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            }}
          >
            {/* Error Header */}
            <Box 
              sx={{ 
                p: 4, 
                pb: 5,
                background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`,
                color: 'white',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  mx: 'auto',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 60, color: 'white' }} />
              </Box>
              
              <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
                Verification Issue
              </Typography>
              
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                We couldn't verify your subscription
              </Typography>
            </Box>
            
            {/* Content */}
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                {error}
              </Alert>
              
              <Typography variant="body1" paragraph color="text.secondary">
                If you completed the payment, your subscription may still be processing. 
                Please check your dashboard or contact support if the issue persists.
              </Typography>
              
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'center',
                  mt: 4,
                }}
              >
                <Button 
                  variant="contained" 
                  color="primary"
                  size="large"
                  startIcon={<DashboardIcon />}
                  onClick={() => navigate('/dashboard')}
                  sx={{ 
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                  }}
                >
                  Go to Dashboard
                </Button>
                
                <Button 
                  variant="outlined"
                  size="large"
                  onClick={() => window.location.reload()}
                  sx={{ 
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                  }}
                >
                  Try Again
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
      {/* Use the simple confetti version */}
      {showConfetti && (
        <SimpleConfetti isActive={showConfetti} />
      )}
      
      <Fade in={true} timeout={800}>
        <Paper 
          elevation={4} 
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          }}
        >
          {/* Header */}
          <Box 
            sx={{ 
              p: 4, 
              pb: 5,
              background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
              color: 'white',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'radial-gradient(circle at top right, rgba(255,255,255,0.15) 0%, transparent 60%)',
                zIndex: 0,
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Grow in={true} timeout={1000}>
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                    mx: 'auto',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 60, color: 'white' }} />
                </Box>
              </Grow>
              
              <Typography variant="h3" component="h1" gutterBottom fontWeight={700}>
                Payment Successful!
              </Typography>
              
              <Typography variant="h6" sx={{ mb: 2, opacity: 0.9, fontWeight: 400 }}>
                Thank you for your subscription
              </Typography>
              
              {amount && (
                <Chip 
                  label={`Amount: ${amount}`} 
                  sx={{ 
                    mt: 2,
                    fontWeight: 600,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '& .MuiChip-label': {
                      px: 2,
                    }
                  }} 
                />
              )}
            </Box>
          </Box>
          
          {/* Content */}
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom fontWeight={600} color="success.main">
              Your subscription is now active
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4 }} color="text.secondary">
              You now have full access to all premium features and content
            </Typography>
            
            {subscriptionData && (
              <Box 
                sx={{ 
                  mb: 4, 
                  p: 3, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                }}
              >
                <Typography variant="subtitle2" color="success.dark" gutterBottom fontWeight={600}>
                  Subscription Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Plan:</strong> {subscriptionData.plan}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Billing:</strong> ${(subscriptionData.amount / 100).toFixed(2)} / {subscriptionData.interval}
                </Typography>
              </Box>
            )}
            
            {paymentId && (
              <Box 
                sx={{ 
                  mb: 4, 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Payment ID:</strong> {paymentId}
                </Typography>
              </Box>
            )}
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              What's Next?
            </Typography>
            
            <Typography variant="body2" paragraph color="text.secondary">
              You can now explore all premium content and features. Visit your dashboard to get started.
            </Typography>
            
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 2, 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'center',
                mt: 4,
              }}
            >
              <Button 
                variant="contained" 
                color="primary"
                size="large"
                startIcon={<DashboardIcon />}
                onClick={() => navigate('/dashboard?payment_success=true')}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                Go to Dashboard
              </Button>
              
              <Button 
                variant="outlined"
                size="large"
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/subscription')}
                sx={{ 
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                Manage Subscription
              </Button>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Container>
  );
}
